import { CurrencyCode } from "@/lib/currency";

import type { useCreatePayment } from "@/services/payments/create-payment";
import type { PlanUser } from "@/services/plans/get-user-plan";
import type { PlanCard, PlanName } from "@/types/plans";
import type { User } from "@/types/user";
import type { TFunction } from "i18next";

export function submitPayment(
  selectedPlan: PlanName | null | undefined,
  user: User | null | undefined,
  createPaymentMutation: ReturnType<typeof useCreatePayment>,
  userOldPlan: PlanUser | undefined,
  isCurrentPlanCheckEnabled?: boolean
) {
  return () => {
    if (!selectedPlan || !user) return;

    if (isCurrentPlanCheckEnabled && userOldPlan && userOldPlan.name === selectedPlan) return;

    createPaymentMutation.mutate({
      data: {
        tier_name: selectedPlan,
      },
    });
  };
}

export const plansPrices: Record<PlanName, { monthlyEUR: number; monthlyUSD: number }> = {
  starter: { monthlyEUR: 0, monthlyUSD: 0 },
  driver: { monthlyEUR: 15, monthlyUSD: 18 },
  collector: { monthlyEUR: 100, monthlyUSD: 115 },
  professional: { monthlyEUR: 250, monthlyUSD: 289 },
};

export function getPlanCards(t: TFunction<"translation", undefined>): PlanCard[] {
  return (Object.keys(plansPrices) as PlanName[]).map((plan) => {
    const { monthlyEUR, monthlyUSD } = plansPrices[plan];
    const cardIndex = plan === "starter" ? 1 : plan === "driver" ? 2 : plan === "collector" ? 3 : 4;

    return {
      plan,
      title: t(`plans.type.${plan}`),
      description: t(`plans.card${cardIndex}.description`),
      list: Array.from({ length: plan === "collector" || plan === "professional" ? 6 : 5 }).map(
        (_, i) => t(`plans.card${cardIndex}.list.item${i + 1}`)
      ),
      price: monthlyEUR,
      priceInDollars: monthlyUSD,
      pricePerYear: t(`plans.card${cardIndex}.pricePerYear`, {
        currency: CurrencyCode.EUR,
        value: (monthlyEUR * 12).toString(),
      }),
      pricePerYearInDollars: t(`plans.card${cardIndex}.pricePerYear`, {
        currency: CurrencyCode.USD,
        value: (monthlyUSD * 12).toString(),
      }),
      type: plan === "professional" ? t(`plans.card${cardIndex}.type.professional`) : undefined,
    };
  });
}

export const gridResponsive = (custom?: Partial<Record<"xs" | "sm" | "md" | "lg", number>>) => {
  return {
    xs: 12,
    sm: 6,
    md: 4,
    lg: 3,
    ...custom,
  };
};
